import { Assistance } from "@b2bportal/air-booking-api";
import { Gender } from "@b2bportal/air-booking-api";
import type {
  Traveler,
  Assistance as TravelerAssistance,
  WheelChair,
} from "@b2bportal/traveler-api";
import { Gender as TravelerGender } from "@b2bportal/traveler-api";

export const isTraveler = (input: unknown): input is Traveler =>
  input != null &&
  typeof input === "object" &&
  (input as Traveler).name != null;

const wheelChairAssistanceMap: Record<
  Exclude<Assistance, "BLND" | "DEAF" | "STCR">,
  keyof WheelChair
> = {
  [Assistance.WCMP]: "manualPower",
  [Assistance.WCLB]: "withLithiumBattery",
  [Assistance.WCHC]: "mustBeCarried",
  [Assistance.WCHS]: "canWalkToSeat",
  [Assistance.WCBD]: "withDryCellBattery",
  [Assistance.WCHR]: "canWalkUpStairs",
  [Assistance.WCBW]: "withWetCellBattery",
  [Assistance.WCOB]: "onBoardPower",
};

const assistanceMap: Record<
  Assistance,
  keyof TravelerAssistance | keyof WheelChair
> = {
  ...wheelChairAssistanceMap,
  [Assistance.BLND]: "blind",
  [Assistance.DEAF]: "deaf",
  [Assistance.STCR]: "stretcher",
};

export const convertPersonAssistance = (assistanceRequired: Assistance[]) => {
  const assistance: TravelerAssistance & { wheelChair: WheelChair } = {
    deaf: false,
    stretcher: false,
    blind: false,
    wheelChair: {
      withLithiumBattery: false,
      canWalkToSeat: false,
      withDryCellBattery: false,
      canWalkUpStairs: false,
      manualPower: false,
      withWetCellBattery: false,
      onBoardPower: false,
      mustBeCarried: false,
    },
  };

  assistanceRequired.forEach((assistanceType) => {
    if (assistanceType in wheelChairAssistanceMap && assistance.wheelChair) {
      const key = assistanceMap[assistanceType];
      assistance.wheelChair[key as keyof WheelChair] = true;
    }
  });

  if (assistanceRequired.includes(Assistance.DEAF)) {
    assistance.deaf = true;
  }

  if (assistanceRequired.includes(Assistance.STCR)) {
    assistance.stretcher = true;
  }

  if (assistanceRequired.includes(Assistance.BLND)) {
    assistance.blind = true;
  }

  return assistance;
};

export const convertTravelerAssistance = (assistance?: TravelerAssistance) => {
  const assistanceRequired: Assistance[] = [];

  if (!assistance) {
    return assistanceRequired;
  }

  if (assistance.deaf) {
    assistanceRequired.push(Assistance.DEAF);
  }

  if (assistance.stretcher) {
    assistanceRequired.push(Assistance.STCR);
  }

  if (assistance.blind) {
    assistanceRequired.push(Assistance.BLND);
  }

  if (assistance?.wheelChair) {
    Object.entries(assistance?.wheelChair).forEach(([key, value]) => {
      if (value) {
        const assistanceType = Object.entries(wheelChairAssistanceMap).find(
          ([, wheelChairKey]) => wheelChairKey === key
        )?.[0] as Assistance;
        if (assistanceType) {
          assistanceRequired.push(assistanceType);
        }
      }
    });
  }

  return assistanceRequired;
};

export const genderMap = {
  [Gender.M]: TravelerGender.Male,
  [Gender.F]: TravelerGender.Female,
};

export const getTravelerGender = (gender?: TravelerGender) => {
  const genderEntry = Object.entries(genderMap).find(
    ([, value]) => value === gender
  );
  return genderEntry ? (genderEntry[0] as Gender) : undefined;
};
